// File path: /src/components/layout/Sidebar.tsx

import { useState, useRef, useCallback, useMemo, memo } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { PanelLeft, X, ChevronRight } from "lucide-react";
import { getIconForLabel } from "@/config/icons";
import { navigation as topNavigation, type NavItem } from "@/components/layout/navbar/navbarConfig";
import SidebarFlyout from "./sidebar/SidebarFlyout";
import SidebarIcon from "./sidebar/SidebarIcon";

// Helper function to check if an item is active based on the current path
const checkItemIsActive = (itemPath: string, currentPath: string): boolean => {
  // Check exact path match first
  if (currentPath === itemPath) return true;
  // Check if path matches without query params
  const pathWithoutQuery = itemPath.split('?')[0];
  if (currentPath === pathWithoutQuery) return true;
  // Special handling for token paths
  if (itemPath === "/app/tokens" && currentPath === "/app/tokens") {
    return true;
  }
  if (itemPath.startsWith("/app/tokens/charts") && currentPath === "/app/tokens/charts") {
    return true;
  }
  // Social paths
  if (itemPath === "/app/social/twitter" && currentPath.startsWith("/app/social/twitter")) {
    return true;
  }
  if (itemPath === "/app/social/youtube" && currentPath.startsWith("/app/social/youtube")) {
    return true;
  }
  if (itemPath === "/app/social/news" && currentPath.startsWith("/app/social/news")) {
    return true;
  }
  // Sector paths
  if (itemPath === "/app/sectors" && currentPath === "/app/sectors") {
    return true;
  }
  if (itemPath === "/app/sectors/compare" && currentPath === "/app/sectors/compare") {
    return true;
  }
  // Workspace paths
  if (itemPath === "/app/workspace/sheets" && currentPath.startsWith("/app/workspace/sheets")) {
    return true;
  }
  if (itemPath === "/app/workspace/dashboards" && currentPath.startsWith("/app/workspace/dashboards")) {
    return true;
  }
  // Macro paths - only match exact paths, not prefix
  if (itemPath.startsWith("/app/macro")) {
    // For /app/macro (overview), only match exact
    if (itemPath === "/app/macro" && currentPath === "/app/macro") {
      return true;
    }
    // For sub-paths like /app/macro/economy, match if pathname starts with item path
    if (itemPath !== "/app/macro" && currentPath.startsWith(itemPath)) {
      return true;
    }
  }
  return false;
};

interface SidebarProps {
  isMobileOpen?: boolean;
  onMobileClose?: () => void;
}

// Simple scale animation for icon hover effects
const getIconAnimation = (): string => {
  return "group-hover:scale-110";
};

const SectionHeader: React.FC<{
  title: string;
  iconName?: string;
  isActive?: boolean;
  onToggle?: () => void;
  open?: boolean;
  collapsed?: boolean;
  onPrimaryAction?: () => void;
  primaryOnly?: boolean;
}> = ({
  title,
  iconName,
  isActive = false,
  onToggle,
  open = true,
  collapsed = false,
  onPrimaryAction,
  primaryOnly = false,
}) => {
  return (
    <button
      className={`group w-full flex items-center justify-between text-sm font-semibold tracking-normal px-2 py-2 transition-colors ${
        isActive ? "text-primary" : "text-white hover:text-primary/80"
      }`}
      onClick={() => {
        if ((collapsed || primaryOnly) && onPrimaryAction) {
          onPrimaryAction();
        } else if (onToggle) {
          onToggle();
        }
      }}
      aria-expanded={open}
      title={title}>
      <div className="flex items-center gap-3">
        {iconName && (
          <div className="w-4 h-4 flex-shrink-0">
            <SidebarIcon
              name={iconName}
              className={`w-4 h-4 transition-all duration-300 ease-in-out ${
                isActive ? "text-primary" : ""
              } ${getIconAnimation()}`}
            />
          </div>
        )}
        <span className={collapsed ? "sr-only" : "uppercase"}>{title}</span>
      </div>
      {onToggle && !collapsed && (
        <div className="w-4 h-4 flex-shrink-0">
          <svg
            className={`w-4 h-4 transition-transform ${
              open ? "rotate-90 text-primary" : "text-base-content/40"
            }`}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </div>
      )}
    </button>
  );
};

const getGroupIconName = (label: string): string => {
  return getIconForLabel(label) ?? "grid-2x2";
};

const getFlatItemIconName = (label: string): string => {
  const mapped = getIconForLabel(label);
  if (label.toLowerCase() === "overview" || label.toLowerCase() === "home")
    return "home";
  if (!mapped || mapped === "dot") return "circle";
  return mapped;
};

const Sidebar: React.FC<SidebarProps> = ({
  isMobileOpen = false,
  onMobileClose,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);

  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({
    Home: false,
    Tokens: true, // Start expanded by default
    Sectors: false,
    Macro: false,
    Indices: false,
    ETFs: false,
    Investors: false,
    "Social Sentiment & News": false,
    Workspace: false,
    Portfolio: false,
    "AI Assistant": false,
  });

  // Memoize active item states to avoid recalculating on every render
  const activeItemsMap = useMemo(() => {
    const map: Record<string, boolean> = {};
    topNavigation.forEach((group) => {
      group.items.forEach((item) => {
        map[item.path] = checkItemIsActive(item.path, location.pathname);
      });
    });
    return map;
  }, [location.pathname]);

  // Memoize active groups to avoid recalculating on every render
  const activeGroupsMap = useMemo(() => {
    const map: Record<string, boolean> = {};
    topNavigation.forEach((group) => {
      map[group.label] = group.items.some((it) => {
        // Home is active on exact /app match
        if (it.path === "/app") {
          return location.pathname === "/app";
        }
        if (location.pathname === it.path) return true;
        // For research items, check if current path starts with the item path
        if (it.path === "/app/tokens") {
          return location.pathname.startsWith("/app/tokens");
        }
        if (it.path === "/app/sectors") {
          return location.pathname.startsWith("/app/sectors");
        }
        if (it.path === "/app/social/twitter") {
          return location.pathname.startsWith("/app/social");
        }
        // Workspace paths
        if (it.path.startsWith("/app/workspace/")) {
          return location.pathname.startsWith(it.path);
        }
        return location.pathname.startsWith(it.path + "/");
      });
    });
    return map;
  }, [location.pathname]);

  // Flyout state management
  const [activeFlyout, setActiveFlyout] = useState<NavItem | null>(null);
  const [flyoutPosition, setFlyoutPosition] = useState<{ top: number } | null>(null);
  const flyoutTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isHoveringFlyoutRef = useRef(false);

  const handleItemMouseEnter = useCallback((item: NavItem, rect: DOMRect) => {
    // Only show flyout for items with flyoutType on desktop and when sidebar is not collapsed
    if (!item.flyoutType || collapsed) return;

    if (flyoutTimeoutRef.current) {
      clearTimeout(flyoutTimeoutRef.current);
    }

    flyoutTimeoutRef.current = setTimeout(() => {
      setActiveFlyout(item);
      setFlyoutPosition({ top: rect.top });
    }, 150);
  }, [collapsed]);

  const handleItemMouseLeave = useCallback(() => {
    if (flyoutTimeoutRef.current) {
      clearTimeout(flyoutTimeoutRef.current);
    }

    flyoutTimeoutRef.current = setTimeout(() => {
      if (!isHoveringFlyoutRef.current) {
        setActiveFlyout(null);
        setFlyoutPosition(null);
      }
    }, 100);
  }, []);

  const handleFlyoutMouseEnter = useCallback(() => {
    isHoveringFlyoutRef.current = true;
    if (flyoutTimeoutRef.current) {
      clearTimeout(flyoutTimeoutRef.current);
    }
  }, []);

  const handleFlyoutMouseLeave = useCallback(() => {
    isHoveringFlyoutRef.current = false;
    flyoutTimeoutRef.current = setTimeout(() => {
      setActiveFlyout(null);
      setFlyoutPosition(null);
    }, 100);
  }, []);

  const handleFlyoutClose = useCallback(() => {
    isHoveringFlyoutRef.current = false;
    setActiveFlyout(null);
    setFlyoutPosition(null);
  }, []);

  const toggleGroup = (label: string) =>
    setOpenGroups((g) => ({ ...g, [label]: !g[label] }));

  const handleNavigate = (path: string) => {
    navigate(path);
    handleFlyoutClose();
    if (onMobileClose) onMobileClose();
  };

  const toggleSidebar = () => {
    setCollapsed(!collapsed);
    handleFlyoutClose();
  };

  // When collapsed, clicking a non-active main button should navigate
  // to the group's specific default route
  const defaultCollapsedTargetByGroup: Record<string, string> = {
    Home: "/app",
    Tokens: "/app/tokens",
    Sectors: "/app/sectors",
    Macro: "/app/macro",
    Indices: "/app/indicators",
    ETFs: "/app/etfs",
    Investors: "/app/investors",
    "Social Sentiment & News": "/app/social/twitter",
    Workspace: "/app/workspace/dashboards",
    Portfolio: "/app/portfolio/overview",
    "AI Assistant": "/app/assistant",
  };

  // Calculate sidebar width for flyout positioning
  const sidebarWidth = collapsed ? 64 : 320;

  const sidebarContent = (
    <div className="w-full flex flex-col h-full">
      {/* Mobile header */}
      <div className="md:hidden flex items-center justify-between p-4 border-b border-gray-700">
        <h2 className="text-lg font-semibold text-white">Navigation</h2>
        <button
          onClick={onMobileClose}
          className="p-2 rounded-md hover:bg-gray-700 transition-colors"
          aria-label="Close mobile menu">
          <X className="w-5 h-5 text-white" />
        </button>
      </div>

      {/* Navigation content */}
      <nav className={`flex-1 px-4 py-4 ${collapsed ? "px-2" : ""}`}>
        <div className="space-y-2">
          {topNavigation.map((group) => {
            // Skip section headers (legacy, not used anymore)
            if (group.isSection) return null;

            // Use memoized active state
            const isActive = activeGroupsMap[group.label] ?? false;

            const isSingleItem = group.items.length === 1;
            const isAssistant = group.label === "AI Assistant";

            return (
              <div key={group.label}>
                <SectionHeader
                  title={group.label}
                  iconName={getGroupIconName(group.label)}
                  isActive={isActive}
                  {...(!isSingleItem && !isAssistant && group.items.length > 0
                    ? { onToggle: () => toggleGroup(group.label) }
                    : {})}
                  open={isAssistant ? true : Boolean(openGroups[group.label])}
                  collapsed={collapsed}
                  primaryOnly={isSingleItem || isAssistant || group.items.length === 0}
                  onPrimaryAction={() => {
                    // For Assistant, open full-screen unless overlay is already visible
                    if (isAssistant) {
                      window.dispatchEvent(new CustomEvent("abi:assistant:open-smart"));
                      if (onMobileClose) onMobileClose();
                      return;
                    }
                    if (isActive) return;
                    const mapped = defaultCollapsedTargetByGroup[group.label];
                    const fallback = group.items[0]?.path;
                    const target = mapped ?? fallback;
                    if (target) {
                      handleNavigate(target);
                      if (onMobileClose) onMobileClose();
                    }
                  }}
                />
                {/* Show sub-items only for multi-item groups when expanded */}
                {!isSingleItem &&
                  !isAssistant &&
                  openGroups[group.label] &&
                  !collapsed &&
                  group.items.length > 0 && (
                    <ul className="mt-1 space-y-0.5 ml-4">
                      {group.items.map((item) => {
                        const hasFlyout = !!item.flyoutType;
                        // Use memoized active state
                        const itemIsActive = activeItemsMap[item.path] ?? false;

                        // Check if this item's flyout is currently active
                        const isFlyoutActive = activeFlyout?.label === item.label;

                        return (
                          <li key={item.path + item.label}>
                            {/* Item with flyout - show flyout on hover (desktop) */}
                            <div
                              className="relative"
                              onMouseEnter={(e) => {
                                const rect = e.currentTarget.getBoundingClientRect();
                                handleItemMouseEnter(item, rect);
                              }}
                              onMouseLeave={handleItemMouseLeave}
                            >
                              <Link
                                to={item.path}
                                className={`group w-full text-left rounded-md transition-colors flex items-start gap-2.5 px-2 py-1.5 ${
                                  itemIsActive || isFlyoutActive
                                    ? "bg-primary/10 text-primary"
                                    : "text-white/80 hover:text-white hover:bg-white/5"
                                }`}
                                onClick={(e) => {
                                  if (
                                    e.button === 0 &&
                                    !e.ctrlKey &&
                                    !e.metaKey &&
                                    !e.shiftKey
                                  ) {
                                    e.preventDefault();
                                    handleNavigate(item.path);
                                    if (onMobileClose) onMobileClose();
                                  }
                                }}
                                title={item.description || item.label}
                              >
                                <div className="w-4 h-4 flex-shrink-0 mt-0.5">
                                  <SidebarIcon
                                    name={getFlatItemIconName(item.label)}
                                    className={`w-4 h-4 transition-all duration-200 ${
                                      itemIsActive || isFlyoutActive ? "text-primary" : "text-white/50"
                                    }`}
                                  />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-1">
                                    <span className="text-sm font-medium">{item.label}</span>
                                    {/* Flyout indicator */}
                                    {hasFlyout && (
                                      <ChevronRight
                                        className={`w-3 h-3 transition-all duration-200 ${
                                          isFlyoutActive
                                            ? "opacity-100 text-primary"
                                            : "opacity-30 group-hover:opacity-60"
                                        }`}
                                      />
                                    )}
                                  </div>
                                  {/* Description */}
                                  {item.description && (
                                    <p className={`text-xs mt-0.5 leading-tight ${
                                      itemIsActive || isFlyoutActive ? "text-primary/60" : "text-white/40"
                                    }`}>
                                      {item.description}
                                    </p>
                                  )}
                                </div>
                              </Link>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
              </div>
            );
          })}
        </div>
      </nav>

      {/* Toggle button pinned at bottom of scroll view (desktop only) */}
      <div className="hidden md:block sticky bottom-0 z-10 flex px-4 py-2 border-t border-white/10 bg-gray-900/80 backdrop-blur supports-[backdrop-filter]:bg-gray-900/60">
        <div className="w-full flex justify-end">
          <button
            onClick={toggleSidebar}
            className="group p-2 rounded-md hover:bg-base-200/60 transition-colors"
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}>
            <PanelLeft
              className="w-4 h-4 transition-transform group-hover:scale-110"
            />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={onMobileClose}
        />
      )}

      {/* Mobile sidebar */}
      <aside
        className={`fixed top-14 left-0 h-[calc(100vh-3.5rem)] w-80 bg-gray-900/95 backdrop-blur z-50 transform transition-transform duration-300 ease-in-out md:hidden ${
          isMobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        aria-label="Mobile navigation sidebar">
        {sidebarContent}
      </aside>

      {/* Desktop sidebar wrapper */}
      <div className="hidden md:block shrink-0 relative">
        {/* Fixed border that extends full page height */}
        <div
          className={`fixed top-14 bottom-0 w-px bg-white/10 z-30 transition-all duration-300 ease-in-out`}
          style={{
            left: collapsed ? '4rem' : '20rem',
            height: '100%',
            minHeight: '100vh'
          }}
        />

        {/* Desktop sidebar */}
        <aside
          className={`flex sticky top-14 h-[calc(100vh-3.5rem)] overflow-y-auto bg-gray-900/70 backdrop-blur supports-[backdrop-filter]:bg-gray-900/50 z-40 transition-all duration-300 ease-in-out ${
            collapsed ? "w-16" : "w-80"
          }`}
          aria-label="Desktop navigation sidebar">
          {sidebarContent}
        </aside>
      </div>

      {/* Flyout panel - desktop only */}
      {activeFlyout && flyoutPosition && !collapsed && (
        <div className="hidden md:block">
          <SidebarFlyout
            item={activeFlyout}
            position={flyoutPosition}
            sidebarWidth={sidebarWidth}
            onClose={handleFlyoutClose}
            onMouseEnter={handleFlyoutMouseEnter}
            onMouseLeave={handleFlyoutMouseLeave}
          />
        </div>
      )}
    </>
  );
};

export default memo(Sidebar);
